import React, { useEffect, useMemo, useState } from "react";
import { Box, Card, CardActions, CardContent, Grid, Typography } from "@mui/material";
import { useClassroom } from "../contexts/classroom.context";
import { useAppNavigate } from "../../../hooks/useAppNavigate";
import { useMobile } from "../../../hooks/useMobile";
import Spinner from "../../layout/Spinner";
import { PrimaryButton, SecondaryButton } from "../../layout/Button/Button";
import { SimpleBlankSlate } from "../../../core/components/SimpleBlankSlate";
import { WizardHeading } from "../../../core/components/WizardHeading";
import { Error as ErrorView } from "../../../core/components/Error";

export const SelectClassroomForProgramme = (): JSX.Element => {
    const { allClassrooms, loading, error, listClassrooms } = useClassroom();
    const { push } = useAppNavigate();
    const { isMobile, isTablet } = useMobile();
    const [listFailed, setListFailed] = useState(false);

    useEffect(() => {
        setListFailed(false);
        listClassrooms(false).catch(() => {
            setListFailed(true);
        });
    }, [listClassrooms]);

    const activeClassrooms = useMemo(() => allClassrooms.filter((classroom) => !classroom.deleted), [allClassrooms]);

    useEffect(() => {
        if (!loading && !listFailed && !error && activeClassrooms.length === 1) {
            push(`/classrooms/view/${activeClassrooms[0].id}/add`);
        }
    }, [activeClassrooms, loading, listFailed, error, push]);

    const startCreateProgramme = (classroomId: number) => {
        push(`/classrooms/view/${classroomId}/add`);
    };

    if (loading || (!listFailed && !error && activeClassrooms.length === 1)) {
        return <Spinner />;
    }

    if (listFailed || error) {
        return (
            <Box p={!isTablet && isMobile ? 2 : 4}>
                {!isTablet && isMobile && <div style={{ paddingTop: "64px" }} />}
                <ErrorView description={error ?? "Unable to load classrooms. Please try again."} />
            </Box>
        );
    }

    if (activeClassrooms.length === 0) {
        return (
            <Box p={!isTablet && isMobile ? 2 : 4}>
                {!isTablet && isMobile && <div style={{ paddingTop: "64px" }} />}
                <SimpleBlankSlate
                    header="No classrooms available"
                    content="Create a classroom before you can schedule a programme."
                />
                <Box mt={2}>
                    <PrimaryButton onClick={() => push("/classrooms/add")}>Create classroom</PrimaryButton>
                </Box>
            </Box>
        );
    }

    return (
        <Box p={!isTablet && isMobile ? 2 : 4}>
            {!isTablet && isMobile && <div style={{ paddingTop: "64px" }} />}
            <WizardHeading overline="Schedule a programme" heading="Select a classroom" />
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                Choose which classroom this programme is for. You will pick a story collection in the next step.
            </Typography>
            <Grid container spacing={2}>
                {activeClassrooms.map((classroom) => (
                    <Grid item xs={12} md={6} key={classroom.id}>
                        <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                            <CardContent sx={{ flex: 1 }}>
                                <Typography variant="h6" component="h2" gutterBottom>
                                    {classroom.name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {classroom.ageRange} Years
                                </Typography>
                                {classroom.description ? (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {classroom.description}
                                    </Typography>
                                ) : null}
                            </CardContent>
                            <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                                <PrimaryButton onClick={() => startCreateProgramme(classroom.id)}>
                                    Create programme
                                </PrimaryButton>
                                <SecondaryButton onClick={() => push(`/classrooms/view/${classroom.id}`)}>
                                    View classroom
                                </SecondaryButton>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};
